/**
 * Family-facing rendering of curated resources, grouped by category.
 * Shared by the parent /circle Resources section and the staff "preview as
 * family" panel so the two surfaces can never drift apart.
 *
 * w1023c L3 (Victor 2026-10-23):
 * - Under Community, TRF groups come first with a "TRF group" chip; the
 *   outside groups follow under the existing "not run by TRF" sentence
 *   (`community:groups.intro`, reused so its translations carry over).
 * - Every entry says when TRF last checked it ("Checked Oct 2026", in the
 *   reader's zone and language). A never-checked entry prints no line.
 * - `reportable` adds the "This didn't work" link and its dialog. Only the
 *   parent page passes it: the staff preview must not post as a family.
 */
import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { ExternalLink as ExternalLinkIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { ExternalLink } from '@/components/ui/ExternalLink';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { useToast } from '@/components/ui/use-toast';
import { MonoLabel, StatusChip } from '@/components/editorial';
import { CATEGORY_ORDER, isTrfGroup, resourceCheckedMonth } from '@/lib/resource-display';
import { RESOURCE_CATEGORY_LABEL_KEY } from '@/lib/circle-display';
import { msg, resolveMsg } from '@/i18n/msg';
import { api } from '@/services/api';
import type { Resource } from '@/types/api';

/** The note's cap; mirrors RESOURCE_REPORT_NOTE_MAX on the server. */
const REPORT_NOTE_MAX = 300;

export function FamilyResourceList({ resources, reportable = false }: { resources: Resource[]; reportable?: boolean }) {
  const { t, i18n } = useTranslation(['circle', 'community']);
  const [reporting, setReporting] = useState<Resource | null>(null);
  const locale = i18n.resolvedLanguage ?? i18n.language ?? 'en';

  const byCategory = CATEGORY_ORDER
    .map((cat) => {
      const items = resources.filter((r) => r.category === cat);
      // TRF groups lead the Community list; everything else keeps its order.
      return { cat, items: cat === 'community' ? [...items.filter(isTrfGroup), ...items.filter((r) => !isTrfGroup(r))] : items };
    })
    .filter((g) => g.items.length > 0);

  const renderCard = (r: Resource) => {
    const month = resourceCheckedMonth(r.last_checked_at, { locale });
    return (
      <Card key={r._id} className="p-5 shadow-none" data-testid="family-resource">
        <div className="flex flex-wrap items-baseline gap-2">
          <h3 translate="no" className="text-card-title font-medium text-foreground">{r.title}</h3>
          {isTrfGroup(r) && <StatusChip tone="teal">{t('circle:resources.trfGroup')}</StatusChip>}
        </div>
        <p translate="no" className="mt-1 text-body text-stevens-ink-3">{r.description}</p>
        {r.url && (
          <ExternalLink href={r.url} className="mt-2 inline-flex items-center gap-1 text-body text-stevens-indigo hover:underline">
            <ExternalLinkIcon className="h-3.5 w-3.5" /> {t('circle:resources.visit')}
          </ExternalLink>
        )}
        {r.contact && <p translate="no" className="mt-1 text-body text-foreground">{r.contact}</p>}
        {r.eligibility_note && <p translate="no" className="mt-2 text-body-sm italic text-stevens-ink-3">{r.eligibility_note}</p>}
        {(month || reportable) && (
          <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-caption text-stevens-ink-3">
            {month && <span data-testid="family-resource-checked">{t('circle:resources.checked', { date: month })}</span>}
            {reportable && (
              <button
                type="button"
                onClick={() => setReporting(r)}
                className="min-h-11 text-caption text-stevens-ink-3 underline underline-offset-2 hover:text-foreground sm:min-h-0"
              >
                {t('circle:resources.report.link')}
              </button>
            )}
          </div>
        )}
      </Card>
    );
  };

  return (
    <>
      {byCategory.map(({ cat, items }) => {
        const outside = cat === 'community' ? items.filter((r) => !isTrfGroup(r)) : [];
        const lead = cat === 'community' ? items.filter(isTrfGroup) : items;
        return (
          <div key={cat} className="space-y-3">
            <MonoLabel className="block">{resolveMsg(t, msg(RESOURCE_CATEGORY_LABEL_KEY[cat]))}</MonoLabel>
            {lead.length > 0 && <div className="grid gap-4 md:grid-cols-2">{lead.map(renderCard)}</div>}
            {outside.length > 0 && (
              <>
                <p className="text-body text-stevens-ink-3" data-testid="outside-groups-note">{t('community:groups.intro')}</p>
                <div className="grid gap-4 md:grid-cols-2">{outside.map(renderCard)}</div>
              </>
            )}
          </div>
        );
      })}
      {reportable && (
        <ReportResourceDialog resource={reporting} onClose={() => setReporting(null)} />
      )}
    </>
  );
}

/**
 * "This didn't work": an optional note (at most 300 characters) and Send.
 * POST /api/circle/resources/:id/report. The reader is the staff list
 * (`trfer-resources`), on its next fetch — another person's screen, so there
 * is nothing on this page to invalidate. No notification fires (Victor
 * 2026-10-23: the row is the signal).
 */
function ReportResourceDialog({ resource, onClose }: { resource: Resource | null; onClose: () => void }) {
  const { t } = useTranslation(['circle', 'common']);
  const { toast } = useToast();
  const [note, setNote] = useState('');
  const close = () => { setNote(''); onClose(); };

  const send = useMutation({
    mutationFn: ({ id, text }: { id: string; text: string }) => api.circle.reportResource(id, text.trim() || null),
    onSuccess: () => { toast({ title: t('circle:resources.report.sent') }); close(); },
    onError: (error: unknown) => {
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status === 409) {
        toast({ title: t('circle:resources.report.tooSoon') });
        close();
        return;
      }
      toast({ variant: 'destructive', title: t('circle:resources.report.failed') });
    },
  });

  return (
    <Dialog open={!!resource} onOpenChange={(o) => { if (!o) close(); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('circle:resources.report.title')}</DialogTitle>
          <DialogDescription>
            <span translate="no" className="font-medium text-foreground">{resource?.title}</span>
            {' · '}
            {t('circle:resources.report.body')}
          </DialogDescription>
        </DialogHeader>
        <label className="flex flex-col gap-1.5 text-body font-medium" htmlFor="resource-report-note">
          {t('circle:resources.report.noteLabel')}
        </label>
        <textarea
          id="resource-report-note"
          value={note}
          maxLength={REPORT_NOTE_MAX}
          rows={3}
          onChange={(e) => setNote(e.target.value)}
          className="w-full rounded-card border border-stevens-rule bg-stevens-surface px-4 py-3 text-body text-stevens-ink focus:outline-none focus:border-stevens-indigo"
        />
        <p className="text-caption text-stevens-ink-3 tabular-nums text-right">{note.length}/{REPORT_NOTE_MAX}</p>
        <DialogFooter>
          <Button variant="outline" onClick={close}>{t('common:actions.cancel')}</Button>
          <Button
            variant="brand"
            disabled={send.isPending || !resource}
            onClick={() => { if (resource) send.mutate({ id: resource._id, text: note }); }}
          >
            {t('circle:resources.report.send')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
